/**
 * Geocoding utilities using Google Geocoding API (preferred) with Nominatim fallback
 */

import { Coordinates, Address } from '../types';

const NOMINATIM_BASE_URL = 'https://nominatim.openstreetmap.org';

/**
 * Geocode using Google Geocoding API (preferred)
 */
const geocodeWithGoogle = async (address: string): Promise<Coordinates | null> => {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  if (!apiKey) {
    return null;
  }

  try {
    const response = await fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${apiKey}`
    );

    if (!response.ok) {
      return null;
    }

    const data = await response.json();
    
    if (data.status === 'OK' && data.results && data.results.length > 0) {
      const location = data.results[0].geometry.location;
      return {
        lat: location.lat,
        lng: location.lng,
      };
    }

    return null;
  } catch (error) {
    console.warn('Google Geocoding API error:', error);
    return null;
  }
};

/**
 * Geocode an address string to coordinates (tries Google first, falls back to Nominatim)
 */
export const geocodeAddress = async (address: string): Promise<Coordinates | null> => {
  // Try Google Geocoding API first
  const googleResult = await geocodeWithGoogle(address);
  if (googleResult) {
    return googleResult;
  }

  // Fallback to Nominatim
  try {
    const response = await fetch(
      `${NOMINATIM_BASE_URL}/search?format=json&q=${encodeURIComponent(address)}&limit=1`,
      {
        headers: {
          'User-Agent': 'LCTF-CRM/1.0', // Required by Nominatim
        },
      }
    );

    if (!response.ok) {
      throw new Error('Geocoding request failed');
    }

    const data = await response.json();
    
    if (data && data.length > 0) {
      return {
        lat: parseFloat(data[0].lat),
        lng: parseFloat(data[0].lon),
      };
    }

    return null;
  } catch (error) {
    console.error('Error geocoding address:', error);
    return null;
  }
};

/**
 * Geocode a structured address object or address string
 */
export const geocodeStructuredAddress = async (address: Address | string): Promise<Coordinates | null> => {
  let addressString: string;
  
  // If it's a string, use it directly
  if (typeof address === 'string') {
    addressString = address.trim();
  } else {
    // Build address string from structured object
    const parts: string[] = [];
    
    if (address.street) parts.push(address.street);
    if (address.city) parts.push(address.city);
    if (address.state) parts.push(address.state);
    if (address.zipCode) parts.push(address.zipCode);
    if (address.country && address.country !== 'United States') parts.push(address.country);
    
    addressString = parts.join(', ');
  }
  
  if (!addressString.trim()) {
    console.warn('Empty address string for geocoding');
    return null;
  }
  
  console.log(`Geocoding address: "${addressString}"`);
  return geocodeAddress(addressString);
};

/**
 * Reverse geocode coordinates to an address
 */
export const reverseGeocode = async (coordinates: Coordinates): Promise<Address | null> => {
  try {
    const response = await fetch(
      `${NOMINATIM_BASE_URL}/reverse?format=json&lat=${coordinates.lat}&lon=${coordinates.lng}`,
      {
        headers: {
          'User-Agent': 'LCTF-CRM/1.0',
        },
      }
    );

    if (!response.ok) {
      throw new Error('Reverse geocoding request failed');
    }

    const data = await response.json();
    
    if (data && data.address) {
      const addr = data.address;
      return {
        street: addr.road || addr.house_number ? `${addr.house_number || ''} ${addr.road || ''}`.trim() : '',
        city: addr.city || addr.town || addr.village || '',
        state: addr.state || addr.region || '',
        zipCode: addr.postcode || '',
        country: addr.country || 'United States',
      };
    }

    return null;
  } catch (error) {
    console.error('Error reverse geocoding:', error);
    return null;
  }
};

/**
 * Batch geocode multiple addresses
 * Note: Nominatim has rate limits, so we add delays between requests
 */
export const batchGeocodeAddresses = async (
  addresses: string[],
  delayMs: number = 1000
): Promise<(Coordinates | null)[]> => {
  const results: (Coordinates | null)[] = [];
  
  for (const address of addresses) {
    const result = await geocodeAddress(address);
    results.push(result);
    
    // Respect rate limits
    if (delayMs > 0) {
      await new Promise(resolve => setTimeout(resolve, delayMs));
    }
  }
  
  return results;
};
